import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import './Messages.css';
import configData from '../../configure';

function Messages({ selectedUser }) {
  const { axios, token, userRole } = useAuth(); // Added userRole
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [messageType, setMessageType] = useState('email');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [attachments, setAttachments] = useState([]);

  const templateNameMap = {
    'PERFORMANCE_APPRECIATION': 'Performance Appreciation',
    'ASSIGNMENT_SUBMISSION': 'Assignment Submission',
    'EVENT_PARTICIPATION': 'Event Participation',
    'ACHIEVEMENT_IN_EVENTS': 'Achievement in Events',
    'PARENT_TEACHER_MEETING_NOTICE': 'Parent-Teacher Meeting Notice',
    'HEALTH_WELLBEING': 'Health & Well-being',
    'ASSIGNMENT_REMINDER': 'Assignment Reminder',
    'EXAM_SCHEDULE': 'Exam Schedule',
    'CLASS_RESCHEDULING': 'Class Rescheduling',
    'HOLIDAY_ANNOUNCEMENT': 'Holiday Announcement',
    'SCHOOL_EVENT': 'School Event',
    'IMPORTANT_ANNOUNCEMENT': 'Important Announcement',
    'HEALTH_SAFETY_AWARENESS': 'Health & Safety Awareness',
  };

  const specificUserIds = selectedUser?.studentInfo?.userId;

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templateUrls = Object.keys(templateNameMap);

        const templateResponses = await Promise.all(
          templateUrls.map(async (url) => {
            try {
              const response = await axios.get(
                `${configData.GET_NOTIFICATION_TEMPLATES}/${url}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/hal+json',
                  },
                }
              );
              return {
                key: url,
                name: templateNameMap[url],
                messageBody: response.data.messageBody || response.data.message || '',
                fullResponse: response.data,
              };
            } catch (error) {
              console.error(`Error fetching template ${url}:`, error);
              return null;
            }
          })
        );

        const validTemplates = templateResponses
          .filter((template) => template !== null)
          .map((template) => ({
            subject: template.name,
            messageBody: template.messageBody,
            fullResponse: template.fullResponse,
          }));

        setTemplates(validTemplates);

        if (validTemplates.length === 0) {
          setStatus({
            type: 'error',
            message: 'No templates could be loaded. Please check your connection.',
          });
        }
      } catch (error) {
        console.error('Error in fetchTemplates:', error);
        setStatus({ type: 'error', message: 'Failed to load message templates' });
      }
    };

    fetchTemplates();
  }, [axios, token]);

  const handleDropdownChange = (event) => {
    const selectedTemplateValue = event.target.value;
    const foundTemplate = templates.find((t) => t.subject === selectedTemplateValue);
    setSelectedTemplate(selectedTemplateValue);
    setMessage(foundTemplate ? foundTemplate.fullResponse : '');
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 10 * 1024 * 1024;
    const validFiles = files.filter((file) => file.size <= maxSize);
    setAttachments(validFiles);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendEmail = async () => {
    if (!specificUserIds?.length || !message?.trim()) {
      setStatus({ type: 'error', message: 'Please select a user and enter a message' });
      setTimeout(() => setStatus({ type: '', message: '' }), 3000);
      return;
    }

    if (!token) {
      setStatus({ type: 'error', message: 'Authentication token missing' });
      setTimeout(() => setStatus({ type: '', message: '' }), 3000);
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      const emailData = {
        subject: selectedTemplate || 'Custom Notification',
        messageBody: message.trim(),
        userType: 'STUDENT',
        specificUserIds: [specificUserIds],
      };

      formData.append('request', new Blob([JSON.stringify(emailData)], { type: 'application/json' }));

      attachments.forEach((file, index) => {
        formData.append(`attachment${index}`, file);
      });

      const response = await axios.post(configData.SEND_EMAIL_NOTIFICATION, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setStatus({ type: 'success', message: response.data?.message || 'Email sent successfully!' });

      setMessage('');
      setSelectedTemplate('');
      setAttachments([]);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send email';
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
      setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    }
  };

  const handleSendSMS = async () => {
    if (!specificUserIds || !message.trim()) {
      setStatus({ type: 'error', message: 'Please select a user and enter a message' });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        configData.SEND_SMS_NOTIFICATION,
        {
          subject: selectedTemplate || 'Custom Notification',
          messageBody: message,
          userType: 'STUDENT',
          specificUserIds: [specificUserIds],
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      setStatus({ type: 'success', message: 'SMS sent successfully!' });
      setMessage('');
    } catch (error) {
      setStatus({ type: 'error', message: error.response?.data?.message || 'Failed to send SMS' });
    } finally {
      setIsLoading(false);
      setTimeout(() => setStatus({ type: '', message: '' }), 3000);
    }
  };

  return (
    <div className="messages-container">
      <div className="top-section">
        <div className="radio-group">
          <label>
            <input
              type="radio"
              value="email"
              checked={messageType === 'email'}
              onChange={() => setMessageType('email')}
            />
            Email
          </label>
          <label>
            <input
              type="radio"
              value="sms"
              checked={messageType === 'sms'}
              onChange={() => setMessageType('sms')}
            />
            SMS
          </label>
        </div>

        <div className="template-section">
          <label htmlFor="template-dropdown">Select Template:</label>
          <select
            id="template-dropdown"
            value={selectedTemplate || ''}
            onChange={handleDropdownChange}
            className="template-select"
          >
            <option value="">-- Select Template --</option>
            {templates.map((template, index) => (
              <option key={index} value={template.subject}>
                {template.subject}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="message-input-section">
        <label htmlFor="message">Notification Message:</label>
        <textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your notification message here..."
          className="message-textarea"
        />
      </div>

      {messageType === 'email' && (
        <div className="">
          {/* <input
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={isLoading}
            accept=".pdf,.doc,.docx,.jpg,.png"
          /> */}
          {attachments.length > 0 && (
            <div className="attachment-list">
              {attachments.map((file, index) => (
                <div key={index} className="attachment-item">
                  <span>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                  <button onClick={() => removeAttachment(index)} disabled={isLoading}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button
        className="send-button"
        onClick={messageType === 'email' ? handleSendEmail : handleSendSMS}
        disabled={isLoading || userRole === 'STUDENT'} 
      >
        {isLoading ? 'Sending...' : 'Send Notification'}
      </button>

      {status?.message && (
        <div className={`status-message ${status.type}`}>{status.message}</div>
      )}
    </div>
  );
}

export default Messages;